import capitalize from "lodash/capitalize";
import isEqual from "lodash/isEqual";
import React, { useEffect, useMemo, useRef } from "react";
import { Redirect, useParams } from "react-router-dom";

import Alert from "veneer/Alert";
import Button from "veneer/Button";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";

import { ROOT_URL } from "cortex/ui/src/fleet/constant/mediaLibrary";
import {
  contentGroupsSelector,
  contentGroupsStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/content-groups/selectors";
import {
  editStrategyAction,
  newStrategyAction,
} from "cortex/ui/src/fleet/redux-store/strategies";
import {
  strategiesSelector,
  strategiesStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/strategies/selectors";
import {
  resetUiRedirectAction,
  updateUiStrategyAction,
} from "cortex/ui/src/fleet/redux-store/ui-state/strategy/actions";
import type { StrategyDetail as StrategyDraft } from "cortex/ui/src/fleet/redux-store/ui-state/strategy/reducers";
import { initialStrategy } from "cortex/ui/src/fleet/redux-store/ui-state/strategy/reducers";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import {
  applicationsStoresLoadedSelector,
  compatApplicationsSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { getAuthUser } from "cortex/ui/src/redux-store/auth";
import {
  userContentsSelector,
  userContentsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/user-contents/selectors";
import { isAdmin, isOperator } from "cortex/ui/src/redux-store/users/selectors";
import BackButton from "cortex/ui/src/ui/button";
import { NavLoader } from "cortex/ui/src/ui/loader";
import { User } from "cortex/ui/src/user/model";
import { Container, Panel } from "cortex/ui/src/view/layout";
import { VIEW, isValidViewType } from "cortex/ui/src/view/views";

import SpotList from "./SpotList";
import {
  DenormalizedStrategy,
  denormalizeStrategy,
  normalizeStrategy,
} from "../utils";

interface StrategyDetailProps {
  applications?: Record<string, unknown>;
  canSubmit: boolean;
  currentStrategy: StrategyDraft;
  editStrategy: (id: number, strategy: unknown) => void;
  id?: string | null;
  newStrategy: (strategy: unknown) => void;
  redirectTo?: number | null;
  referenceDnStrategy: StrategyDraft | DenormalizedStrategy | null;
  resetUiRedirect?: () => void;
  setUiStrategy: (strategy: StrategyDraft) => void;
  storesLoaded: boolean;
  strategy: StrategyDraft;
  user?: User | null;
  viewType: string;
}

function canEditStrategy(user: User | null | undefined, viewType: string) {
  if (isAdmin(user)) {
    return true;
  }

  return viewType === VIEW.CLONE && isOperator(user);
}

function renderRedirect(strategyId: number) {
  const pathname = `${ROOT_URL}/${strategyId}/${VIEW.EDIT}`;

  return (
    <Redirect
      to={{
        pathname: "/redirect",
        search: `to=${pathname}`,
      }}
    />
  );
}

export function StrategyDetail({
  applications = {},
  canSubmit,
  currentStrategy,
  editStrategy,
  id = null,
  newStrategy,
  redirectTo,
  referenceDnStrategy,
  setUiStrategy,
  storesLoaded,
  strategy,
  user,
  viewType,
}: StrategyDetailProps) {
  const strategyHasChanged = !isEqual(referenceDnStrategy, currentStrategy);
  const editable = canEditStrategy(user, viewType);

  const handleNameChange = (name: string | null) => {
    const nextName = name ?? "";

    if (nextName === strategy.name) {
      return;
    }

    setUiStrategy({
      ...strategy,
      name: nextName,
    });
  };

  const saveStrategy = () => {
    const normalizedStrategy = normalizeStrategy(applications, strategy as any);

    if ([VIEW.CLONE, VIEW.CREATE].includes(viewType as never)) {
      newStrategy(normalizedStrategy as any);
      return;
    }

    if (id == null) {
      return;
    }

    const strategyId = Number(id);
    if (Number.isNaN(strategyId)) {
      return;
    }

    editStrategy(strategyId, normalizedStrategy as any);
  };

  if (!isValidViewType(viewType as any)) {
    return <Redirect to="/404" />;
  }

  if (redirectTo) {
    return renderRedirect(redirectTo);
  }

  return (
    <Container hideBox title={`${capitalize(viewType)} Strategy`}>
      {!storesLoaded ? (
        <NavLoader.Proxy />
      ) : (
        <Panel gap={2}>
          {strategyHasChanged && (
            <Alert severity="info">You may have unsaved changes.</Alert>
          )}
          <TextInput
            disabled={!editable}
            fullWidth
            label="Strategy Name"
            onChange={handleNameChange}
            required
            value={strategy.name ?? ""}
          />
          <SpotList
            disabled={!isAdmin(user)}
            priority="main"
            spots={strategy.main as any}
            strategy={strategy as any}
          />
          <SpotList
            disabled={!isAdmin(user)}
            priority="fallback"
            spots={strategy.fallback as any}
            strategy={strategy as any}
          />
          <Stack
            alignItems="center"
            direction="row"
            gap={1}
            justifyContent="flex-end"
          >
            <BackButton />
            <Button disabled={!canSubmit} onClick={saveStrategy}>
              Save
            </Button>
          </Stack>
        </Panel>
      )}
    </Container>
  );
}

export default function StrategyDetailPage() {
  const dispatch = useAppDispatch();
  const initializedRouteRef = useRef<string | null>(null);
  const { id = null, viewType = "" } = useParams<{
    id?: string;
    viewType?: string;
  }>();
  const dnStrategy = useAppSelector(
    (state: any) => state.pages.strategyDetail.strategy,
  );
  const redirectTo = useAppSelector(
    (state: any) => state.pages.strategyDetail.redirectTo,
  );
  const user = useAppSelector(getAuthUser);
  const contentGroups = useAppSelector(contentGroupsSelector);
  const contentGroupsLoaded = useAppSelector(contentGroupsStoresLoadedSelector);
  const userContents = useAppSelector(userContentsSelector);
  const userContentsLoaded = useAppSelector(userContentsStoresLoadedSelector);
  const applications = useAppSelector(compatApplicationsSelector);
  const applicationsStoresLoaded = useAppSelector(
    applicationsStoresLoadedSelector,
  );
  const strategies = useAppSelector(strategiesSelector);
  const strategiesLoaded = useAppSelector(strategiesStoresLoadedSelector);
  const strategy = id === null ? null : strategies[id] || null;
  const storesLoaded = useMemo(
    () =>
      applicationsStoresLoaded &&
      contentGroupsLoaded &&
      strategiesLoaded &&
      userContentsLoaded,
    [
      applicationsStoresLoaded,
      contentGroupsLoaded,
      strategiesLoaded,
      userContentsLoaded,
    ],
  );

  const referenceDnStrategy =
    id === null || !storesLoaded
      ? initialStrategy
      : denormalizeStrategy(
          applications,
          userContents,
          contentGroups,
          strategy as any,
        );

  useEffect(() => {
    return () => {
      dispatch(resetUiRedirectAction);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!storesLoaded) {
      initializedRouteRef.current = null;
      return;
    }

    const routeKey = `${id ?? "create"}:${viewType}`;
    if (initializedRouteRef.current === routeKey) {
      return;
    }

    if (referenceDnStrategy) {
      dispatch(
        updateUiStrategyAction({
          ...referenceDnStrategy,
          name:
            viewType === VIEW.CLONE
              ? `Clone - ${referenceDnStrategy.name}`
              : referenceDnStrategy.name,
        } as any),
      );
    }

    initializedRouteRef.current = routeKey;
  }, [dispatch, id, referenceDnStrategy, storesLoaded, viewType]);

  const currentStrategy = (dnStrategy ?? initialStrategy) as StrategyDraft;
  const canSubmit = Boolean(
    dnStrategy &&
      !isEqual(dnStrategy, referenceDnStrategy) &&
      dnStrategy.name &&
      dnStrategy.main[0],
  );

  if (storesLoaded && !referenceDnStrategy) {
    return <Redirect to="/404" />;
  }

  return (
    <StrategyDetail
      applications={applications}
      canSubmit={canSubmit}
      currentStrategy={currentStrategy}
      editStrategy={(strategyId, nextStrategy) => {
        dispatch(
          editStrategyAction(
            strategyId,
            nextStrategy as any,
            "Success! Strategy edited.",
          ),
        );
      }}
      id={id}
      newStrategy={(nextStrategy) => {
        dispatch(
          newStrategyAction(
            nextStrategy as any,
            "Success! New strategy created.",
          ),
        );
      }}
      redirectTo={redirectTo}
      referenceDnStrategy={referenceDnStrategy}
      setUiStrategy={(nextStrategy) => {
        dispatch(updateUiStrategyAction(nextStrategy as any));
      }}
      storesLoaded={storesLoaded}
      strategy={currentStrategy}
      user={user}
      viewType={viewType}
    />
  );
}
